import { FC, Suspense } from 'react'
import { QueryErrorResetBoundary, useSuspenseQuery } from '@tanstack/react-query'
import { ErrorBoundary } from 'react-error-boundary'
import { Alert, Box, Button, Image, Modal, Stack, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'

import { getRawPhotosOptions } from '@/api/navigationApi'
import { Client } from '@/api/main/client'
import useBlobUrl from '@/hooks/useBlobUrl'
import { CenteredLoader } from '@/components/CenteredLoader'

export const getCameraPreviewLayout = () => ({
  frameHeight: '44vh',
  imageWidth: '100%',
  imageHeight: '100%',
  imageFit: 'contain' as const,
  imagePosition: 'center' as const,
})

const CameraCheckInner: FC<{
  client: Client | undefined
  onConfirm: () => void
  onCancel: () => void
}> = ({ client, onConfirm, onCancel }) => {
  const { t } = useTranslation(['navigation', 'errors'])
  const previewLayout = getCameraPreviewLayout()
  const { data: capture } = useSuspenseQuery({
    ...getRawPhotosOptions({ client }),
    refetchOnMount: 'always',
    retry: 1,
    staleTime: 0,
    gcTime: 0,
  })

  const captureUrl = useBlobUrl(capture)

  return (
    <Stack align="center" justify="center" gap="md" p="xl">
      <Text fw={700} ta="center" fz="var(--spacing-xl)">
        {t('cameraCheckTitle')}
      </Text>
      <Box
        w="100%"
        h={previewLayout.frameHeight}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Image
          src={captureUrl}
          w={previewLayout.imageWidth}
          h={previewLayout.imageHeight}
          fit={previewLayout.imageFit}
          style={{ objectPosition: previewLayout.imagePosition }}
        />
      </Box>
      <Button onClick={onConfirm} fullWidth color="teal">
        {t('ok').toUpperCase()}
      </Button>
      <Button onClick={onCancel} fullWidth color="red">
        {t('no').toUpperCase()}
      </Button>
    </Stack>
  )
}

const CameraCheck: FC<{
  client: Client | undefined
  onSuccess: () => void
  onCancel: () => void
  visible: boolean
}> = ({ client, onSuccess, onCancel, visible }) => {
  const { t } = useTranslation(['navigation', 'errors'])

  return (
    <Modal
      opened={visible}
      onClose={onCancel}
      fullScreen
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      zIndex={10000}
    >
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary
            onReset={reset}
            fallbackRender={({ resetErrorBoundary }) => (
              <Stack align="center" justify="center" p="xl">
                <Alert title={t('couldNotFetchCameraCapture', { ns: 'errors' })}>
                  <Button onClick={resetErrorBoundary}>Try again</Button>
                </Alert>
              </Stack>
            )}
          >
            <Suspense fallback={<CenteredLoader />}>
              <CameraCheckInner client={client} onConfirm={onSuccess} onCancel={onCancel} />
            </Suspense>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </Modal>
  )
}

export default CameraCheck
